import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import styles from './LegalPages.module.css';

/**
 * Veřejné právní stránky (bez přihlášení) — odkazuje na ně Google Play
 * (zásady ochrany osobních údajů + postup smazání účtu) i patička úvodky.
 */

const CONTACT = 'stavebnidokumenty@gmail.com';
const EFFECTIVE = '26. 9. 2026';

function LegalLayout({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className={styles.page}>
      <nav className={styles.nav}>
        <Link to="/" className={styles.logo} aria-label="Úvodní stránka">
          <span className={styles.logoMark}>S</span>
          <span className={styles.logoName}>SiteDoc</span>
        </Link>
        <Link to="/" className={styles.back}>
          ← Úvodní stránka
        </Link>
      </nav>
      <main className={styles.main}>
        <h1 className={styles.title}>{title}</h1>
        <p className={styles.effective}>Účinné od {EFFECTIVE}</p>
        {children}
      </main>
      <LegalFooter />
    </div>
  );
}

export function LegalFooter() {
  return (
    <footer className={styles.footer}>
      <Link to="/ochrana-osobnich-udaju">Ochrana osobních údajů</Link>
      <span aria-hidden>·</span>
      <Link to="/smazani-uctu">Smazání účtu</Link>
      <span aria-hidden>·</span>
      <a href={`mailto:${CONTACT}`}>{CONTACT}</a>
    </footer>
  );
}

const Mail = () => <a href={`mailto:${CONTACT}`}>{CONTACT}</a>;

export function PrivacyPage() {
  return (
    <LegalLayout title="Zásady ochrany osobních údajů">
      <p>
        Tyto zásady popisují, jak webová aplikace SiteDoc (sitedoc.cz) a její aplikace pro Android
        (balíček <code>cz.sitedoc.app</code>) zpracovávají osobní údaje. Android aplikace otevírá
        stejnou webovou aplikaci, platí pro ni proto totéž co pro web.
      </p>

      <h2>1. Správce</h2>
      <p>
        Ing. David Harant, e-mail: <Mail />. Na tento e-mail pište ve všech věcech týkajících se
        osobních údajů.
      </p>

      <h2>2. Jaké údaje zpracováváme</h2>
      <ul>
        <li>
          <strong>Údaje o účtu</strong> — jméno, e-mailová adresa a heslo (ukládá se jen v zašifrované
          podobě jako hash, v čitelné podobě ho neznáme).
        </li>
        <li>
          <strong>Obsah, který do aplikace zadáte</strong> — stavby, dokumenty, stavební deník,
          fotografie, podpisy, firmy a kontaktní osoby v adresáři, fakturační údaje, kniha jízd,
          vyúčtování, karty zaměstnanců, úkoly a další data. Za údaje třetích osob, které do
          aplikace vložíte (např. kontakty, zaměstnanci), odpovídáte vy; my je zpracováváme jen
          proto, abychom vám aplikaci poskytli.
        </li>
        <li>
          <strong>Technické údaje</strong> — IP adresa a údaje o požadavku v provozních záznamech
          serveru; u zapnutých notifikací adresa pro doručení push notifikace od vašeho prohlížeče.
        </li>
        <li>
          <strong>Úložiště v zařízení</strong> — aplikace si v prohlížeči (localStorage) ukládá
          přihlašovací token, rozepsané koncepty dokumentů a vaše volby. Nepoužíváme reklamní ani
          analytické cookies a neměříme návštěvnost.
        </li>
      </ul>

      <h2>3. Proč a na jakém základě</h2>
      <ul>
        <li>
          Provoz aplikace a vašeho účtu, uložení a zpracování zadaných dat, odeslání e-mailů a
          notifikací, o které požádáte — plnění smlouvy (čl. 6 odst. 1 písm. b GDPR).
        </li>
        <li>
          Zabezpečení a řešení chyb (provozní záznamy serveru) — oprávněný zájem (čl. 6 odst. 1
          písm. f GDPR).
        </li>
      </ul>
      <p>Údaje neprodáváme, nepoužíváme k reklamě ani k profilování.</p>

      <h2>4. Kde jsou data uložena a kdo se k nim dostane</h2>
      <p>
        Data jsou uložena na serveru společnosti Hetzner Online GmbH v datovém centru v Helsinkách
        (Finsko, EU). Při některých funkcích aplikace komunikuje s dalšími službami:
      </p>
      <ul>
        <li>
          <strong>Google (Gmail)</strong> — odesílání e-mailů z aplikace (dokumenty, obnova hesla,
          upozornění). Předává se adresa příjemce a obsah zprávy.
        </li>
        <li>
          <strong>Google Fonts</strong> — načtení písma; prohlížeč přitom předá IP adresu serverům
          Google.
        </li>
        <li>
          <strong>ARES (Ministerstvo financí ČR)</strong> — vyhledání firmy podle IČO nebo názvu.
        </li>
        <li>
          <strong>OpenStreetMap (Nominatim, mapové podklady) a unpkg.com</strong> — hledání adresy a
          zobrazení mapy při výběru místa stavby.
        </li>
        <li>
          <strong>Open-Meteo</strong> — počasí do stavebního deníku podle polohy stavby.
        </li>
        <li>
          <strong>Služba push notifikací vašeho prohlížeče</strong> (např. Google, Mozilla, Apple) —
          doručení notifikace, jen pokud je zapnete. Obsah notifikace je šifrovaný.
        </li>
      </ul>
      <p>
        Dokumenty a data vidí další lidé jen tehdy, když je sami sdílíte nebo odešlete e-mailem
        (sdílení staveb, kontrolních dnů, projektové dokumentace, úkolů, vyúčtování apod.).
      </p>

      <h2>5. Jak dlouho údaje uchováváme</h2>
      <p>
        Po dobu trvání účtu. Když účet smažete, účet i všechna jeho data se okamžitě a nevratně
        odstraní z databáze. Deaktivovaný účet se nemaže — data zůstávají, dokud ho neobnovíte nebo
        nesmažete. Provozní záznamy serveru uchováváme jen po dobu nezbytnou pro zabezpečení a
        řešení chyb. Postup smazání je na stránce <Link to="/smazani-uctu">Smazání účtu</Link>.
      </p>

      <h2>6. Vaše práva</h2>
      <p>
        Máte právo na přístup ke svým údajům, jejich opravu, výmaz, omezení zpracování,
        přenositelnost a právo vznést námitku. Žádost pošlete na <Mail />, vyřídíme ji nejpozději do
        jednoho měsíce. Můžete také podat stížnost u Úřadu pro ochranu osobních údajů (
        <a href="https://uoou.gov.cz" target="_blank" rel="noopener noreferrer">
          uoou.gov.cz
        </a>
        ).
      </p>

      <h2>7. Oprávnění Android aplikace</h2>
      <p>
        Aplikace přistupuje k fotoaparátu a souborům jen ve chvíli, kdy sami vyberete fotografii
        nebo dokument k nahrání. Notifikace posílá jen po vašem povolení. Polohu zařízení
        nesleduje.
      </p>

      <h2>8. Děti</h2>
      <p>Aplikace je určena pro profesní použití ve stavebnictví, není určena dětem do 16 let.</p>

      <h2>9. Změny zásad</h2>
      <p>
        Zásady můžeme upravit; aktuální znění je vždy na této stránce s datem účinnosti.
      </p>
    </LegalLayout>
  );
}

export function DeleteAccountInfoPage() {
  return (
    <LegalLayout title="Smazání účtu SiteDoc">
      <p>
        Účet v aplikaci SiteDoc (web sitedoc.cz i aplikace pro Android) můžete kdykoli smazat sami
        přímo v aplikaci.
      </p>

      <h2>Postup v aplikaci</h2>
      <ol>
        <li>
          Přihlaste se na <Link to="/login">sitedoc.cz</Link> nebo v aplikaci SiteDoc.
        </li>
        <li>Klepněte na kolečko s iniciálami vpravo nahoře (menu účtu).</li>
        <li>Dole v menu zvolte <strong>Smazat účet</strong>.</li>
        <li>
          Zadejte heslo, napište slovo <strong>SMAZAT</strong> a potvrďte tlačítkem{' '}
          <strong>Smazat účet i data</strong>.
        </li>
      </ol>

      <h2>Co se smaže</h2>
      <p>
        Okamžitě a nevratně se smaže účet (jméno, e-mail, heslo) a všechna data, která k němu
        patří: stavby, dokumenty a archiv, stavební deník, fotografie, podpisy, adresář firem,
        fakturační profily, kniha jízd, vyúčtování, karty zaměstnanců, úkoly, sdílení a nastavení
        notifikací. Nic z toho neuchováváme.
      </p>
      <p>
        Pokud chcete data zachovat, můžete účet místo smazání jen <strong>deaktivovat</strong>{' '}
        (stejné menu, volba „Deaktivovat účet“). Data zůstanou uložená a účet obnovíte sami dalším
        přihlášením.
      </p>

      <h2 id="jednotlive-udaje">Smazání jednotlivých údajů bez zrušení účtu</h2>
      <p>
        Účet rušit nemusíte — jednotlivé údaje smažete přímo v aplikaci u dané položky (ikona koše
        nebo tlačítko Smazat). Smazání je okamžité a nevratné. Takto lze smazat:
      </p>
      <ul>
        <li>celou stavbu včetně všeho, co k ní patří;</li>
        <li>uložené dokumenty v Archivu a podepsané dokumenty;</li>
        <li>fotografie, soubory projektové dokumentace a přílohy deníku a kontrolních dnů;</li>
        <li>vady, dotazy k projektu a změnové listy;</li>
        <li>firmy v adresáři, vlastní fakturační profily, loga, uložené podpisy a šablony;</li>
        <li>úkoly a jejich výstupy, poznámky, karty zaměstnanců a jejich položky;</li>
        <li>položky, vklady a převody ve vyúčtování, vozidla a měsíce v knize jízd;</li>
        <li>historii e-mailových adres a sdílení s jinými uživateli.</li>
      </ul>
      <p>
        Pokud něco z toho v aplikaci smazat nejde, napište na <Mail /> — smažeme to do 30 dnů.
      </p>

      <h2>Nemůžete se přihlásit?</h2>
      <p>
        Nejdřív zkuste <Link to="/forgot-password">obnovu hesla</Link>. Pokud to nejde, pošlete
        žádost o smazání z e-mailové adresy, na kterou je účet založen, na <Mail />. Účet i data
        smažeme nejpozději do 30 dnů a potvrdíme vám to e-mailem.
      </p>
    </LegalLayout>
  );
}
